<script setup lang="ts">
import { ESumSubAction } from '~/interfaces/dto/compliance/request';

const { setPrimary, data } = useMobileNumberHandler();
const { $t, $trackEvent, $enabled } = useNuxtApp();
const siteStore = useSiteStore();
const authStore = useAuthStore();

const isLoading = ref<boolean>(false);
const showSumSub = ref<boolean>(false);

const sumSubEnabled = computed(() =>
  $enabled('security.highriskeventsprofilemanagement'),
);

const confirmHandler = async () => {
  if (sumSubEnabled.value) showSumSub.value = true;
  else await acknowledge();
};

const acknowledge = async () => {
  showSumSub.value = false;
  isLoading.value = true;
  const response = await setPrimary(data.desiredPrimaryNumber);

  isLoading.value = false;
  if (response?.isSuccessful) {
    $trackEvent('Account', 'MY_ACCOUNT_CHANGE_PRIMARY_NUMBER');
    siteStore.deactivateModal();
    authStore.logout({ reload: true, redirect: true, path: '/?login=1' });
  } else {
    data.errorCode = String(response?.error?.displayCode ?? 'general');
    data.errorRef = String(response?.error?.code ?? '');
  }
};

const declineHandler = () => {
  data.desiredPrimaryNumber = data.primaryNumber.mobileNumber;
  siteStore.deactivateModal();
};

onBeforeUnmount(() => {
  data.errorCode = '';
  data.errorRef = '';
});
</script>

<template>
  <UserSumsub
    v-model="showSumSub"
    :action="ESumSubAction.MobileNumberChange"
    @complete="acknowledge"
  />
  <LazyUserInterfaceGenericLoader v-if="isLoading" :standalone="true" />
  <div v-else-if="!showSumSub && !isLoading" class="p-4 flex flex-col gap-3">
    <UserInterfaceGenericError
      :state="data.errorCode ? 'danger' : 'warn'"
      class="max-w-72 mx-auto mb-2"
      :reference-code="data.errorRef"
      v-bind="{
        ...(data.errorCode && {
          'reference-code': data.errorRef,
        }),
      }"
    >
      {{
        data.errorCode
          ? $t(`update-details-${data.errorCode}`)
          : $t('acknowledge-primary-number-change-logout-alert-body')
      }}
    </UserInterfaceGenericError>
  </div>
  <div
    v-if="!showSumSub && !isLoading"
    class="flex gap-4 p-4 bg-base w-full"
  >
    <DesignSystemButton
      type="secondary-alternative"
      class="w-full justify-center"
      @click="declineHandler"
    >
      {{ $t('decline') }}
    </DesignSystemButton>
    <DesignSystemButton
      type="primary"
      class="w-full justify-center"
      @click="confirmHandler"
    >
      {{ $t('confirm') }}
    </DesignSystemButton>
  </div>
</template>
